import { doc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import {
  Proprietario,
  Inquilino,
  RateRiscaldamento,
  Rate,
  UnitaImmobiliare,
} from "../types/firestoreTypes";
import { InitialAnalysisResult, DeeperAnalysisResult } from "../types/analysisTypes";

// ---- NEW: OpenAI client (Chat Completions API) ----
import OpenAI from "openai";

// 🔑 Cache for the API key & client
let cachedOpenAIApiKey: string | null = null;
let openaiClient: OpenAI | null = null;

async function getOpenAIApiKey(): Promise<string> {
  if (cachedOpenAIApiKey) return cachedOpenAIApiKey;

  const snapshot = await getDocs(collection(db, "open_ai_key"));

  if (snapshot.empty) {
    throw new Error(
      "No document found in Firestore collection 'open_ai_key' (expected 1 doc with field 'api_key')."
    );
  }

  const docSnap = snapshot.docs[0];
  const data = docSnap.data() as { api_key?: string };

  if (!data.api_key) {
    throw new Error(
      "Field 'api_key' not found on the document in 'open_ai_key' collection."
    );
  }

  cachedOpenAIApiKey = data.api_key;
  return cachedOpenAIApiKey;
}

async function getOpenAIClient(): Promise<OpenAI> {
  if (openaiClient) return openaiClient;

  const apiKey = await getOpenAIApiKey();
  openaiClient = new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true, // ⚠️ frontend usage, fine for now
  });
  return openaiClient;
}


// ---- Small helpers for logging & bounds ----
const MAX_LLM_CANDIDATES = 140;   // keep payload reasonable
const MAX_ALTS = 3;

function safeSlice<T>(arr: T[], n: number) {
  return arr.length > n ? arr.slice(0, n) : arr;
}

function normalizeBasic(s?: string): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // diacritics
    .replace(/[^\p{L}\p{N}\s]/gu, " ")               // keep letters/digits/spaces
    .replace(/\s+/g, " ")
    .trim();
}

// ---- LLM decider: single call, strict JSON result ----
type LlmCandidate = { id: string; name: string; role: "P" | "I" };
type LlmVerdict = {
  best_id: string | null;
  alt_ids: string[];
  confidence: "high" | "medium" | "low" | "none";
  score: number; // 0..1
  reason: string; // short
};

async function llmPickBest(query: string[], candidates: LlmCandidate[]): Promise<LlmVerdict> {
  const list = safeSlice(candidates, MAX_LLM_CANDIDATES);

  console.info("[llm] query:", query);
  console.info("[llm] candidates:", list.length);

  // Chat Completions tool schema (NOT Responses API)
  const tools: any[] = [
    {
      type: "function",
      function: {
        name: "choose_match",
        description: "Choose the best matching candidate for the given query, or none.",
        parameters: {
          type: "object",
          additionalProperties: false,
          properties: {
            best_id: { type: ["string", "null"] },
            alt_ids: {
              type: "array",
              items: { type: "string" },
              maxItems: MAX_ALTS,
              uniqueItems: true
            },
            confidence: { type: "string", enum: ["high", "medium", "low", "none"] },
            score: { type: "number", minimum: 0, maximum: 1 },
            reason: { type: "string", maxLength: 200 }
          },
          required: ["best_id", "alt_ids", "confidence", "score", "reason"]
        }
      }
    }
  ];

  const system = [
    "You are a precise matching engine for Italian condominium records.",
    "Given a query name and a list of candidates (id, role, name), pick the most likely match.",
    "Prefer strong lexical overlap on distinctive tokens; ignore generic words (e.g., 'condominio', 'immobiliare', 'societa').",
    "Be robust to abbreviations, punctuation and diacritics.",
    "If there is no good match, return best_id = null and confidence = 'none'.",
    "Return your answer ONLY by calling the provided function with JSON arguments."
  ].join(" ");

  const userPayload = {
    query_name: query,
    // query_name_normalized: normalizeBasic(query),
    candidates: list.map(c => ({
      id: c.id,
      role: c.role,
      name: c.name,
      name_normalized: normalizeBasic(c.name),
    })),
  };

  const openai = await getOpenAIClient();  // ⬅️ use client with Firestore key
  
  // Use Chat Completions API
  const completion = await openai.chat.completions.create({
    model: "gpt-5-mini",
    temperature: 1,
    messages: [
      { role: "system", content: system },
      // Send instruction and the JSON payload as plain text to keep types happy
      { role: "user", content: "Pick the best match. Use the function and ONLY the function." },
      { role: "user", content: JSON.stringify(userPayload) },
    ],
    tools,
    tool_choice: { type: "function", function: { name: "choose_match" } }, // force structured output
  });

  const msg = completion.choices?.[0]?.message;

  // Type guard for function tool calls
  type FuncCall = { type: "function"; function: { name: string; arguments: string } };

  const funcCall: FuncCall | undefined = (msg?.tool_calls as any[] | undefined)?.find(
    (tc: any): tc is FuncCall => tc && tc.type === "function" && tc.function && typeof tc.function.arguments === "string"
  );

  if (!funcCall) {
    console.warn("[llm] No function tool call returned; defaulting to none.");
    return { best_id: null, alt_ids: [], confidence: "none", score: 0, reason: "no_tool_call" };
  }

  let args: LlmVerdict | undefined;
  try {
    args = JSON.parse(funcCall.function.arguments) as LlmVerdict;
  } catch (e) {
    console.warn("[llm] Failed to parse tool args:", e);
    return { best_id: null, alt_ids: [], confidence: "none", score: 0, reason: "bad_json" };
  }

  if (!args) {
    console.warn("[llm] Empty tool args; defaulting to none.");
    return { best_id: null, alt_ids: [], confidence: "none", score: 0, reason: "empty_args" };
  }

  // Guardrails
  const idSet = new Set(list.map(c => c.id));
  const best_id = args.best_id && idSet.has(args.best_id) ? args.best_id : null;
  const alt_ids = Array.isArray(args.alt_ids)
    ? args.alt_ids.filter(id => idSet.has(id) && id !== best_id).slice(0, MAX_ALTS)
    : [];
  const confidence = (["high", "medium", "low", "none"] as const).includes(args.confidence as any)
    ? args.confidence
    : "none";
  const score = typeof args.score === "number" ? Math.max(0, Math.min(1, args.score)) : 0;
  const reason = String(args.reason || "");

  console.info("[llm] verdict:", { best_id, alt_ids, confidence, score, reason });
  return { best_id, alt_ids, confidence, score, reason };
}

// ---- Main function: use LLM decision for proprietari/inq ----
export async function analyzeDeeperTransaction(
  initialAnalysis: InitialAnalysisResult,
  condominioId: string
): Promise<DeeperAnalysisResult> {
  console.time("[analyzeDeeperTransaction] total");

  // Build the query string from names + mittente
  let { names } = initialAnalysis;
  const { mittente } = initialAnalysis;
  try {
    names.push(mittente);
  } catch {
    names = [mittente];
  }
  //const raw = (names || []).map(n => (n ?? "").trim()).find(n => n.length > 0) || "";
  const raw = (names || [])
  .map(n => (n ?? "").trim())
  .filter(n => n.length > 0);
  if (!raw) throw new Error("No valid names to match.");
  console.info("[analyzeDeeperTransaction] Raw query:", raw);

  // Fetch Firestore (unchanged)
  console.time("[firestore] fetch");
  const condominioRef = doc(db, "condomini", condominioId);
  const [propSnapshot, inqSnapshot, uiSnapshot, rateSnapshot, rateRisSnapshot] =
    await Promise.all([
      getDocs(collection(condominioRef, "proprietari")),
      getDocs(collection(condominioRef, "inquilini")),
      getDocs(collection(condominioRef, "unita_immobiliari")),
      getDocs(collection(condominioRef, "rate")),
      getDocs(collection(condominioRef, "rate_riscaldamento")),
    ]);
  console.timeEnd("[firestore] fetch");

  const proprietariDocs = propSnapshot.docs.map(d => ({ id: d.id, ...d.data() }) as unknown as Proprietario);
  const inquiliniDocs   = inqSnapshot.docs.map(d => ({ id: d.id, ...d.data() }) as unknown as Inquilino);
  const unitaImmobiliariDocs = uiSnapshot.docs.map(d => ({ id: d.id, ...d.data() }) as unknown as UnitaImmobiliare);
  const rateDocs        = rateSnapshot.docs.map(d => ({ id: d.id, ...d.data() }) as Rate);
  const rateRisDocs     = rateRisSnapshot.docs.map(d => ({ id: d.id, ...d.data() }) as RateRiscaldamento);

  console.info(`[firestore] proprietari=${proprietariDocs.length}, inquilini=${inquiliniDocs.length}`);

  // Build one flat candidate list (id, name, role)
  const candidates: LlmCandidate[] = [
    ...proprietariDocs
      .filter(p => !!(p as any).ac_nom)
      .map(p => ({ id: `P:${(p as any).cod_condomino}`, name: String((p as any).ac_nom), role: "P" as const })),
    ...inquiliniDocs
      .filter(i => !!(i as any).iq_nom)
      .map(i => ({ id: `I:${(i as any).cod_inquilino}`, name: String((i as any).iq_nom), role: "I" as const })),
  ];

  // Optional super-light pruning: keep the top N by simple token overlap count
//  const qSet = new Set(normalizeBasic(raw).split(" ").filter(Boolean));
//  const counted = candidates.map(c => {
//    const cSet = new Set(normalizeBasic(c.name).split(" ").filter(Boolean));
//    let overlap = 0; for (const t of qSet) if (cSet.has(t)) overlap++;
//    return { c, overlap };
//  });
//  counted.sort((a, b) => b.overlap - a.overlap);
//  const pruned = safeSlice(counted.map(x => x.c), MAX_LLM_CANDIDATES);
//
//  console.info(`[llm] sending ${pruned.length}/${candidates.length} candidates after light prune`);
  const verdict = await llmPickBest(raw, candidates);

  // Map verdict back
  let matchedProprietari: Proprietario[] = [];
  let matchedInquilini: Inquilino[] = [];

  const idToP = new Map<string, Proprietario>();
  const idToI = new Map<string, Inquilino>();
  for (const p of proprietariDocs) idToP.set(String((p as any).cod_condomino), p);
  for (const i of inquiliniDocs)   idToI.set(String((i as any).cod_inquilino), i);

  const take = (id: string | null) => {
    if (!id) return;
    if (id.startsWith("P:")) {
      const key = id.slice(2);
      const hit = idToP.get(key);
      if (hit) matchedProprietari.push(hit);
    } else if (id.startsWith("I:")) {
      const key = id.slice(2);
      const hit = idToI.get(key);
      if (hit) matchedInquilini.push(hit);
    }
  };

  take(verdict.best_id);
  for (const alt of verdict.alt_ids) take(alt);
  console.log(verdict);


  // Dedup
  const dedupByCodCondomino = <T extends { cod_condomino: string }>(arr: T[]) => {
    const seen = new Set<string>();
    return arr.filter(x => (seen.has(x.cod_condomino) ? false : (seen.add(x.cod_condomino), true)));
  };
  const dedupByCodInquilino = <T extends { cod_inquilino: string }>(arr: T[]) => {
    const seen = new Set<string>();
    return arr.filter(x => (seen.has(x.cod_inquilino) ? false : (seen.add(x.cod_inquilino), true)));
  };
  matchedProprietari = dedupByCodCondomino(matchedProprietari);
  matchedInquilini = dedupByCodInquilino(matchedInquilini);

  console.info(`[llm] matched P=${matchedProprietari.length}, I=${matchedInquilini.length}, conf=${verdict.confidence}, score=${verdict.score}`);

  // Join related records (same as before)
  const matchedUnitaImmobiliari = unitaImmobiliariDocs.filter(u =>
    matchedProprietari.some(p => (p as any).cod_condomino === (u as any).cod_condomino) ||
    matchedInquilini.some(i => (i as any).cod_inquilino === (u as any).cod_inquilino)
  );

  const matchedRates = rateDocs.filter(r =>
    matchedProprietari.some(p => (p as any).cod_condomino === (r as any).cod_condomino)
  );

  const matchedRateRis = rateRisDocs.filter(r =>
    matchedUnitaImmobiliari.some(u => (u as any).cod_ui === (r as any).cod_ui)
  );

  console.info(`[join] ui=${matchedUnitaImmobiliari.length}, rate=${matchedRates.length}, rate_risc=${matchedRateRis.length}`);

  // Map to your legacy filter levels (optional)
  const levelMap = { high: 3, medium: 2, low: 1, none: 0 } as const;
  const confLevel = levelMap[verdict.confidence] ?? 0;

  const result: DeeperAnalysisResult = {
    initial_analysis: initialAnalysis,
    matched_proprietari: matchedProprietari,
    matched_inquilini: matchedInquilini,
    matched_unita_immobiliari: matchedUnitaImmobiliari,
    matched_rates: matchedRates,
    matched_rate_ris: matchedRateRis,
    proprietari_filter_level: matchedProprietari.length ? confLevel : 0,
    inquilini_filter_level: matchedInquilini.length ? confLevel : 0,
  };

  console.log("[result] LLM:", verdict);
  console.timeEnd("[analyzeDeeperTransaction] total");
  return result;
}